<template>
    <p id="treegrid-desc" class="sr-only">{{ $t('preventif.suivi.description') }}</p>
    <table aria-describedby="treegrid-desc">
        <caption class="sr-only">{{ $t('preventif.suivi.title') }}</caption>
        <thead class="header">
            <tr v-if="gridHeader.length === 0">
                <th scope="col">
                </th>
                <th
                    scope="col"
                    :id="`th-month-${index}`"
                    :class="`${index === 0 ? 'first-cell' : index === 8 ? 'last-cell' : ''}`"
                    v-for="index in 12"
                    :key="index"
                >
                    <img
                        @click="this.previous"
                        @keydown.enter.prevent="this.previous"
                        @keydown.space.prevent="this.previous"
                        tabindex="0"
                        role="button"
                        class="chevron-left"
                        v-if="index === 1"
                        :src="this.chevronLeft"
                        :alt="$t('alt.page.precedente')"
                    />
                    <img
                        @click="this.next"
                        @keydown.enter.prevent="this.next"
                        @keydown.space.prevent="this.next"
                        tabindex="0"
                        role="button"
                        class="chevron-right"
                        v-if="index === 12"
                        :src="this.chevronRight"
                        :alt="$t('alt.page.suivante')"
                    />
                </th>
            </tr>
            <tr v-else>
                <th scope="col" id="th-expand-collapse" :class="!isWeek ? 'small-header': ''">
                    <span>
                        <img @click="this.upAll" @keydown.enter.prevent="this.upAll" @keydown.space.prevent="this.upAll" tabindex="0" role="button" class="upAll" :src="this.chevronUp" :alt="$t('alt.deplier')" />
                        <img @click="this.downAll" @keydown.enter.prevent="this.downAll" @keydown.space.prevent="this.downAll" tabindex="0" role="button" class="downAll" :src="this.chevronDown" :alt="$t('alt.replier')" />
                    </span>
                </th>
                <th
                    scope="col"
                    :id="`th-period-${index}`"
                    :class="`${!isWeek ? 'text-center' : ''} ${
                        dayjs(dayjs().format('YYYY-MM-DD')).isBetween(
                            dayjs(headerItem.dateDebut).subtract(1, 'd'),
                            dayjs(headerItem.dateFin).add(1, 'd')
                        )
                            ? 'current-date'
                            : ''
                    } ${index === 0 ? 'first-cell' : index === gridHeader.length - 1 ? 'last-cell' : ''}`"
                    v-for="(headerItem, index) in gridHeader"
                    :key="headerItem"
                >
                    <img
                        @click="this.previous"
                        @keydown.enter.prevent="this.previous"
                        @keydown.space.prevent="this.previous"
                        tabindex="0"
                        role="button"
                        class="chevron-left"
                        v-if="index === 0"
                        :src="this.chevronLeft"
                        :alt="$t('alt.page.precedente')"
                    />
                    <template v-if="isWeek">
                        <div class="month-weekly">
                            <div class="year">
                                <span
                                    v-if="
                                        index === 0 ||
                                        new Date(headerItem.dateDebut).getFullYear() !==
                                            new Date(gridHeader[index - 1].dateDebut).getFullYear()
                                    "
                                >
                                    {{ new Date(headerItem.dateDebut).getFullYear() }}
                                </span>
                            </div>
                            <div class="month month-weekly">
                                <span
                                    v-if="
                                        index === 0 ||
                                        new Date(gridHeader[index - 1].dateDebut).getMonth() !==
                                            new Date(headerItem.dateDebut).getMonth()
                                    "
                                >
                                    {{ $t(`month.${new Date(headerItem.dateDebut).getMonth()}`) }}
                                </span>
                            </div>
                            <div class="date-weekly">
                                <span>
                                    {{ $t('from.date') }} {{ new Date(headerItem.dateDebut).toLocaleDateString() }}
                                </span>
                            </div>
                            <div class="date-weekly">
                                <span>
                                    {{ $t('to.date') }} {{ new Date(headerItem.dateFin).toLocaleDateString() }}
                                </span>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="year">
                            <span v-if="index === 0 || new Date(headerItem.dateDebut).getMonth() === 0">
                                {{ new Date(headerItem.dateDebut).getFullYear() }}
                            </span>
                        </div>
                        <div class="month">
                            <span>
                                {{ $t(`month.${new Date(headerItem.dateDebut).getMonth()}`) }}
                            </span>
                        </div>
                    </template>
                    <img
                        @click="this.next"
                        @keydown.enter.prevent="this.next"
                        @keydown.space.prevent="this.next"
                        tabindex="0"
                        role="button"
                        class="chevron-right"
                        v-if="index === gridHeader.length - 1"
                        :src="this.chevronRight"
                        :alt="$t('alt.page.suivante')"
                    />
                </th>
            </tr>
            <tr v-if="gridHeader.length > 0">
                <th scope="col">
                </th>
                <th scope="col" v-for="headerItem in gridHeader" :key="headerItem"></th>
            </tr>
        </thead>
        <tbody v-if="isLoading">
            <tr>
                <td colspan="13">
                    <div class="noData">
                        <LoaderComponent v-if="isLoading" class="loader" :aria-label="$t('search.isLoading')" />
                    </div>
                </td>
            </tr>
        </tbody>
        <tbody v-else-if="gridHeaderAll.length > 0 && gridData.length !== 0">
            <TableRow
                v-for="(item, i) in gridData"
                :rowData="item"
                :key="item.title"
                :rowClass="RowClass.first"
                :openAll="openAll"
                :selectionMode="selectionMode"
                :selected-report-ids="selectedReportIds"
                :selected-ui-ids="selectedUiIds"
                @emitOpenModal="this.emitOpenModal"
                @open="this.handleEvent"
                @cellClick="$emit('cellClick', $event)"
                :index="i"
            />
            <tr v-if="otherDataLoading">
                <td class="noDataTd" colspan="13">
                    {{ $t('search.isLoading') }}
                </td>
            </tr>
        </tbody>
        <tbody v-else>
            <tr>
                <td colspan="13">
                    <div class="noData">{{ $t('preventif.no.data') }}</div>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script lang="ts">
import TableRow from '@/modules/Preventif/components/TableRow.vue';
import { defineComponent, PropType } from 'vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';

import dayjs from 'dayjs';

export interface TableRowData {
    title: string;
    noeudsEnfants: TableRowData[];
}

export enum RowClass {
    first = 'first',
    second = 'second',
    third = 'third',
    fourth = 'fourth',
}

export default defineComponent({
    components: { TableRow, LoaderComponent },
    setup() {
        return { RowClass };
    },
    props: {
        gridData: { type: Array as PropType<TableRowData[]>, required: true },
        gridHeader: { type: Array, required: true },
        isWeek: { type: Boolean, required: true },
        isLoading: { type: Boolean, required: true },
        otherDataLoading: { type: Boolean, required: true },
        gridHeaderAll: { type: Array, required: true },
        selectionMode: { type: Boolean, required: false, default: false },
        selectedReportIds: { type: Array as PropType<string[]>, required: false, default: () => [] },
        selectedUiIds: { type: Array as PropType<string[]>, required: false, default: () => [] },
    },
    data(): { openAll?: boolean } {
        return {
            openAll: undefined,
        };
    },
    computed: {
        checkboxMarkedCircle() {
            return require('@/assets/img/checkbox-marked-circle.svg');
        },
        checkboxMarkedCircleSemi() {
            return require('@/assets/img/checkbox-marked-circle-semi.svg');
        },
        checkboxMarkedCircleVoid() {
            return require('@/assets/img/checkbox-marked-circle-void.svg');
        },
        chevronUp() {
            return require('@/assets/img/chevron-up.svg');
        },
        chevronDown() {
            return require('@/assets/img/chevron-down.svg');
        },
        chevronLeft() {
            return require('@/assets/img/chevron-left.svg');
        },
        chevronRight() {
            return require('@/assets/img/chevron-right.svg');
        },
    },
    methods: {
        dayjs(date?: Date) {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const isBetween = require('dayjs/plugin/isBetween');

            dayjs.extend(isBetween);
            return dayjs(date);
        },
        handleEvent() {
            this.openAll = undefined;
        },
        emitOpenModal(id: string) {
            this.$emit('emitOpenModal', id);
        },
        upAll() {
            this.openAll = false;
        },
        downAll() {
            this.openAll = true;
        },
        next() {
            this.$emit('next');
        },
        previous() {
            this.$emit('previous');
        },
    },
});
</script>

<style scoped>
table {
    background: white;
    border-collapse: collapse;
    width: 100%;
    border-radius: 4px;
    margin-bottom: 16px;
}
table thead tr:first-child {
    border-bottom: 1px solid #c4c4c4;
}
table thead tr:first-child th:first-child {
    height: 90px;
    padding: 0;
    text-align: center;
}
table thead tr:first-child th:first-child.small-header{
    height: 70px;
}
table thead tr:first-child th {
    font-family: 'Raleway Semi Bold', sans-serif;
    border-top: none;
}
table td:first-child, table th:first-child {
    border-left: none;
}
table td:last-child, table th:last-child {
    border-right: none;
}
table td, table th {
    width: 7%;
    position: relative;
    border-left: 1px solid #c4c4c4;
    border-right: 1px solid #c4c4c4;
    padding: 0.59% 0.24% 0.59% 0.24%;
}
table tr {
    margin-top: 4px;
}
table tbody td:not(:first-child) span {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
}
table tbody td:first-child span {
    display: flex;
    align-items: center;
}
table tbody td:first-child span {
    cursor: pointer;
}
.first {
    background: rgba(0, 0, 0, 0.2);
}
.second {
    background: rgba(0, 0, 0, 0.1);
}
.third {
    background: rgba(0, 0, 0, 0.05);
}
.second > td:first-child span {
    padding-left: 20px;
}
.third > td:first-child span {
    padding-left: 40px;
}
.fourth > td:first-child span {
    padding-left: 60px;
}
.upAll,
.downAll {
    cursor: pointer;
    width: 2vw;
}
.downAll {
    margin-left: 20px;
}
.title {
    font-family: 'Raleway Semi Bold', sans-serif;
}
.year {
    top: 8px;
    left: 8px;
    height: 16px;
    font-size: var(--font-size-medium);
    color: var(--dark-grey);
    text-decoration: underline;
    font-family: 'Raleway', sans-serif;
    text-align: center;
    font-weight: lighter;
}
.month {
    font-size: var(--font-size-low-medium);
    color: var(--dark-grey);
    font-family: 'Raleway Semi Bold', sans-serif;
    text-align: center;
    font-weight: 600;
    margin: 10px 2px 5px 2px;
    height: var(--font-size-low-medium);
}

.first-cell {
    margin-right: 8px;
}

.date-weekly {
    text-align: center;
    font-size: var(--font-size-very-small);
    font-family: 'Raleway Semi Bold', sans-serif;
    font-weight: var(--font-weight-normal);
}

.first-cell .week-header-item {
    margin-left: 1.8vw;
}

.current-week-header {
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
}
.current-week-header span {
    font-size: 20px;
}
.current-week-header .bold {
    font-family: 'Raleway Semi Bold', sans-serif;
}
.current-date,
.current-date :not(.year) {
    color: var(--dark-blue) !important;
}
.current-date {
    border-bottom: 2px solid var(--dark-blue);
}
.first-day,
.last-day {
    box-sizing: border-box;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
}
.last-day {
    border-left: 1px solid #c4c4c4;
}
.chevron-left,
.chevron-right {
    position: absolute;
    cursor: pointer;
    top: 0;
    width: 30px;
}
.chevron-left {
    left: -4px;
}
.chevron-right {
    right: -4px;
}
.text-center {
    text-align: center !important;
}
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}
.noData {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
}
.noDataTd {
    text-align: center;
}
.header {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 1;
}
</style>
